import React from 'react';
import { Grid, Dialog, DialogContent, DialogActions, Button, Typography } from '@mui/material';

interface WarningIconProps {
  gradientId: string;
}

export interface LockOwner {
  userId: string;
  userName: string;
  timestamp: number;
  lastActivity: number;
}

/**
 * Warning icon component used in all modals
 */
const WarningIcon: React.FC<WarningIconProps> = ({ gradientId }) => (
  <Grid sx={{ margin: '0 auto', mb: 2, width: 48, height: 42 }}>
    <svg width="48" height="42" viewBox="0 0 48 42">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9C27B0" />
          <stop offset="100%" stopColor="#C58ED4" />
        </linearGradient>
      </defs>
      <path 
        d="M24 2 L44 38 L4 38 Z" 
        fill={`url(#${gradientId})`} 
        rx="3"
        ry="3"
      />
      <text 
        x="24" 
        y="30" 
        textAnchor="middle" 
        fill="white" 
        fontSize="20" 
        fontWeight="bold"
        fontFamily="inherit"
      >
        !
      </text>
    </svg>
  </Grid>
);

export interface UserLockedModalProps {
  open: boolean;
  shouldActivate: boolean;
  lockOwner: LockOwner | null;
}

/**
 * Modal shown when another user has the lock
 */
export const UserLockedModal: React.FC<UserLockedModalProps> = ({ 
  open, 
  shouldActivate, 
  lockOwner 
}) => (
  <Dialog 
    open={open && shouldActivate} 
    disableEscapeKeyDown 
    onClose={() => {}}
    data-testid="user-locked-modal"
    PaperProps={{
      sx: {
        width: '300px',
        borderRadius: 2,
        padding: 2,
        textAlign: 'center'
      }
    }}
  >
    <DialogContent sx={{ padding: 3, paddingBottom: 2 }}>
      <Grid sx={{ mb: 2 }}>
        <WarningIcon gradientId="triangleGradient" />
      </Grid>
      
      <Typography 
        variant="body1" 
        sx={{ 
          fontSize: '16px',
          lineHeight: 1.5,
          color: '#333',
          mb: 1
        }}
      >
        <strong>{lockOwner?.userName || 'Another user'}</strong> is currently in this board. 
        Please try again after some time.
      </Typography>
    </DialogContent>
  </Dialog>
);

export interface TimeoutWarningModalProps {
  open: boolean;
  shouldActivate: boolean;
  timeoutCountdown: number;
  onContinueWorking: () => Promise<void>;
  onSaveAndLeave: () => void;
  onLeave: () => void;
}

/**
 * Modal shown for inactivity timeout warning
 */
export const TimeoutWarningModal: React.FC<TimeoutWarningModalProps> = ({ 
  open, 
  shouldActivate, 
  timeoutCountdown,
  onContinueWorking,
  onSaveAndLeave,
  onLeave 
}) => (
  <Dialog 
    open={open && shouldActivate} 
    disableEscapeKeyDown 
    onClose={() => {}}
    data-testid="timeout-warning-modal"
    PaperProps={{
      sx: {
        width: '400px',
        borderRadius: 3,
        padding: 3,
        textAlign: 'center'
      }
    }}
  >
    <DialogContent sx={{ padding: 3, paddingBottom: 2 }}>
      <Grid sx={{ mb: 3 }}>
        <WarningIcon gradientId="timeoutTriangleGradient" />
      </Grid>
      
      <Typography 
        variant="body1" 
        sx={{ 
          fontSize: '16px',
          lineHeight: 1.5,
          color: '#333',
          mb: 2
        }}
      >
        You are being timed out due to inactivity. Please choose to continue working or leave. 
        Otherwise, you will be logged out from this board automatically.
      </Typography>

      <Typography 
        variant="body2" 
        sx={{ 
          fontSize: '14px',
          color: '#666',
          mb: 3
        }}
      >
        Auto logout in {timeoutCountdown} seconds
      </Typography>
    </DialogContent>
    
    <DialogActions sx={{ padding: 3, paddingTop: 0, flexDirection: 'column', gap: 1 }}>
      <Button 
        variant="contained"
        onClick={() => {
          onContinueWorking().catch(error => 
            console.error('Error continuing work:', error)
          );
        }}
        sx={{ 
          width: '100%',
          backgroundColor: '#333',
          color: 'white',
          '&:hover': {
            backgroundColor: '#555'
          }
        }}
      >
        Continue Working
      </Button>
      
      <Button 
        variant="outlined"
        onClick={onSaveAndLeave}
        sx={{ 
          width: '100%',
          borderColor: '#ddd',
          color: '#333',
          '&:hover': {
            borderColor: '#bbb',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        Save and Leave
      </Button>
      
      <Button 
        variant="outlined"
        onClick={onLeave}
        sx={{ 
          width: '100%',
          borderColor: '#ddd',
          color: '#333',
          '&:hover': {
            borderColor: '#bbb',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        Leave
      </Button>
    </DialogActions>
  </Dialog>
);

export interface LoggedOutModalProps {
  open: boolean;
  onOkay: () => void;
}

/**
 * Modal shown after user has been logged out due to inactivity
 */
export const LoggedOutModal: React.FC<LoggedOutModalProps> = ({ 
  open, 
  onOkay 
}) => (
  <Dialog 
    open={open} 
    disableEscapeKeyDown 
    onClose={() => {}}
    PaperProps={{
      sx: {
        width: '400px',
        borderRadius: 3,
        padding: 3,
        textAlign: 'center'
      }
    }}
  >
    <DialogContent sx={{ padding: 3, paddingBottom: 2 }}>
      <Grid sx={{ mb: 3 }}>
        <WarningIcon gradientId="loggedOutTriangleGradient" />
      </Grid>
      
      <Typography 
        variant="body1" 
        sx={{ 
          fontSize: '16px',
          lineHeight: 1.5,
          color: '#333',
          mb: 3
        }}
      >
        You have been logged out of the board due to inactivity. 
        You will be redirected to the projects page on clicking "Okay".
      </Typography>
    </DialogContent>
    
    <DialogActions sx={{ padding: 3, paddingTop: 0, justifyContent: 'center' }}>
      <Button 
        variant="contained"
        onClick={onOkay}
        sx={{ 
          width: '200px',
          backgroundColor: '#333',
          color: 'white',
          '&:hover': {
            backgroundColor: '#555'
          }
        }}
      >
        Okay
      </Button>
    </DialogActions>
  </Dialog>
);